import { FunctionComponent, useEffect, useState } from 'react';
import {
	Box,
	H2,
	HStack,
	Link,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { useLocation, useParams } from 'react-router-dom';
import BackButton from 'components/common/BackButton/BackButton';
import { LabelAndValue } from 'components/common/LabelAndValue/LabelAndValue';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import SectionDivider from 'components/common/SectionDivider';
import { BUNDLES_GET_BUNDLE, SUBSCRIPTIONS_API_ROUTE } from 'apiRoutes';
import { useGet } from 'hooks/useGet';
import { SubscriptionBundle } from 'domain/subscription/interfaces/SubscriptionBundle';
import SubscriptionUsersTable from './SubscriptionUsersTable';
import { useSubscriptionFormContext } from './SubscriptionForm/SubscriptionFormContext';
import {
	getSubscriptionStatusColor,
	Subscription,
} from 'domain/subscription/interfaces/Subscription';
import { SubscriptionAuditLog } from './SubscriptionAuditLog';
import { RequestLoader } from 'components/common/Loader';
import StyledLink from 'components/common/Link';
import AssignSeatSection from './AssignSeatSection/AssignSeatSection';
import { SubscriberType } from 'domain/subscription/interfaces/SubscriberType';
import SubscriptionSystemEntitiesTable from './SubscriptionSystemEntitiesTable';
import { RequestStatus } from 'types/RequestStatus';
import ApplicationPermissionTabs from './Permissions/ApplicationPermissionTabs';
import { useGlobalContext } from 'components/common/GlobalContext/GlobalContext';
import NotFound from 'components/common/NotFound/NotFound';
import { SubscriptionActions } from './SubscriptionActions';

const SubscriptionPage: FunctionComponent = () => {
	const { id } = useParams();

	if (!id) return null;

	const { toApplicationPermissions, permissionOptionsLoaded } =
		useGlobalContext();

	const {
		data: subscription,
		status,
		error,
		getData: getSubscription,
	} = useGetOnMount<Subscription>(
		`${SUBSCRIPTIONS_API_ROUTE}/GetSubscription?SubscriptionId=${id}`,
	);

	// Refetch subscription when ID changes
	useEffect(() => {
		if (id) {
			getSubscription();
		}
	}, [id]);

	const { data: bundle, getData: getBundle } = useGet<SubscriptionBundle>(
		subscription?.bundleId
			? `${BUNDLES_GET_BUNDLE}?bundleId=${subscription.bundleId}`
			: '',
	);

	useEffect(() => {
		if (subscription?.bundleId) {
			getBundle();
		}
	}, [subscription]);

	const { setActiveSubscription } = useSubscriptionFormContext();

	const [usersLoaded, setUsersLoaded] = useState(false);

	useEffect(() => {
		setActiveSubscription(subscription);
	}, [subscription]);

	const onUserUnassigned = () => {
		getSubscription();
	};

	const location = useLocation();

	useEffect(() => {
		if (location.hash && usersLoaded) {
			const element = document.getElementById(location.hash.substring(1));
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		}
	}, [location, location.hash, usersLoaded]);

	if (status === RequestStatus.NOT_FOUND) {
		return (
			<NotFound title="Subscription Not Found" error={error}>
				<SubscriptionAuditLog subscriptionId={id} />
			</NotFound>
		);
	}

	return (
		<RequestLoader h="500px" status={status}>
			<Box bg="bgMainContainer">
				<BackButton />
				<Box bg="bgMainContainer">
					<VStack gap="xl">
						<HStack fullWidth justify="space-between">
							<H2>Subscription Details</H2>
							<SubscriptionActions
								subscription={subscription}
								onSubscriptionUpdated={getSubscription}
							/>
						</HStack>
						<HStack fullWidth gap="xl">
							<LabelAndValue
								minWidth="30%"
								label="Description"
								value={subscription?.description || '-'}
							/>
							{subscription?.bundleId && (
								<LabelAndValue
									minWidth="30%"
									label="In bundle"
									value={
										<Link
											href={`/subscription-bundles/${subscription.bundleId}`}
										>
											<HStack align="center">
												{subscription.bundleId}
											</HStack>
										</Link>
									}
								/>
							)}
						</HStack>
						<HStack fullWidth gap="xl">
							<LabelAndValue
								minWidth="30%"
								label="Type"
								value={subscription?.type}
							/>
							<LabelAndValue
								minWidth="30%"
								label="Status"
								textColor={getSubscriptionStatusColor(
									subscription?.status,
								)}
								value={subscription?.status}
							/>
						</HStack>
						<HStack fullWidth gap="xl">
							<LabelAndValue
								minWidth="30%"
								label="Start Date"
								value={toDefaultDateFormatOrNotAvailable(
									subscription?.startDate,
								)}
							/>
							<LabelAndValue
								minWidth="30%"
								label="End Date"
								value={toDefaultDateFormatOrNotAvailable(
									subscription?.endDate,
								)}
							/>
						</HStack>
					</VStack>
					<SectionDivider />
					<VStack gap="xl">
						<H2>Ownership</H2>
						<HStack fullWidth gap="xl">
							<LabelAndValue
								minWidth="30%"
								label="Company"
								value={
									subscription?.companyId ? (
										<StyledLink
											to={`/companies/${subscription.companyId}`}
										>
											{subscription.companyName}
										</StyledLink>
									) : (
										'-'
									)
								}
							/>
							<LabelAndValue
								minWidth="30%"
								label="Seats (Occupied/Total)"
								value={`${subscription?.occupiedSeats}/${subscription?.seats}`}
							/>
						</HStack>
					</VStack>
					<SectionDivider />
					<VStack gap="xl">
						<H2>Permissions</H2>
						{subscription && permissionOptionsLoaded && (
							<ApplicationPermissionTabs
								applicationsPermissions={toApplicationPermissions(
									subscription?.applicationPermissions,
								)}
								bundle={bundle}
							/>
						)}
					</VStack>
					<SectionDivider />
					{subscription?.subscriberType ===
					SubscriberType.SystemEntity ? (
						<>
							<H2>System Entities</H2>
							<HStack>
								<SubscriptionSystemEntitiesTable
									subscriptionId={parseInt(id)}
									onUserUnassigned={onUserUnassigned}
								/>
							</HStack>
							<AssignSeatSection
								subscription={subscription}
								getSubscription={getSubscription}
							/>
						</>
					) : (
						<>
							<H2 id="users">Users</H2>
							<HStack>
								<SubscriptionUsersTable
									subscription={subscription}
									onUserUnassigned={onUserUnassigned}
									onUsersLoaded={() => setUsersLoaded(true)}
								/>
							</HStack>
							<AssignSeatSection
								subscription={subscription}
								getSubscription={getSubscription}
							/>
						</>
					)}
					<SectionDivider />
					<SubscriptionAuditLog subscriptionId={id} />
				</Box>
			</Box>
		</RequestLoader>
	);
};

export default SubscriptionPage;
